/**
 * What the navigation chart shows, and how scans uncover it. Pure functions (no DOM / Node APIs) shared by the phone client
 * and scripts/nav-reveal-test.ts.
 *
 * REVEAL STATE is one `Set<string>` (it is what is saved with a game and passed between views):
 *   - "tileId:nodeId"            a chamber has been discovered (drawn as its footprint)
 *   - "~x|y|z|r"                 a SWEPT DISC: everything of a passage within r metres of (x, y, z) has been seen. A passage is
 *                                drawn where it lies inside a swept disc, so a tunnel appears exactly as far as it was swept, even
 *                                when neither end node is known yet. Discs are quantised (and de-duplicated) so the set stays small.
 *
 * Coordinates: map x / z, and the tile JSON's layered local Y (depth = -(baseDepth + y)); 1 sonar metre = 1 map unit.
 *
 * SCAN RULES (what each ping type reveals; "range" is the ping's range in metres):
 *   terrain     The sonar pulse sees the walls it hits. Every ray hit that lies on a chamber's boundary discovers that chamber and every
 *               hit inside a tunnel discovers that stretch of tunnel (a disc of the tunnel's half width + a margin around the hit).
 *               If no hits are supplied (sonar view not ready) the open-water network the pulse can reach is used instead: chambers
 *               within range along connected passages, and the passage lengths leading to them.
 *   subsurface  Sees THROUGH rock: every chamber whose volume comes within `range` of the submarine is discovered, and every passage
 *               is drawn for the length that lies within `range` (one disc of radius `range`).
 *   resource    Finds resource deposits only (the server marks them discovered); the chart's chambers and passages are untouched.
 *   visual      The submarine itself: the chamber it is inside is discovered, and while it is in a chamber or passage the stretch of
 *               passage within VISUAL_REACH_METERS of it is drawn (the sub's lights / short-range sonar).
 */
import { clipPolylineToSpans, connectionPolyline, nodeVolumeDistance, polylineClosest, polylineLength, polylineSphereSpans, mergeSpans, type Vec3T } from './navShapes.js';
import type { AggregatedConnection, TileJsonNode } from './tileJson.js';

export type ScanType = 'terrain' | 'resource' | 'subsurface';

export const DISC_PREFIX = '~';
/** Metres of passage drawn around the submarine while it is in a chamber / passage. */
export const VISUAL_REACH_METERS = 25;
/** A ray hit this close to a chamber's volume counts as hitting its wall (mesh roughness, footprint vs carved shape). */
export const HIT_NODE_MARGIN_METERS = 4;
/** A ray hit this close (beyond the half width) to a passage's centre line counts as hitting that tunnel. */
export const HIT_PASSAGE_MARGIN_METERS = 4;
/** Extra radius of a swept disc around a terrain hit, beyond the tunnel half width. */
export const HIT_DISC_PADDING_METERS = 6;
/** Grid the centres of discs snap to, so repeated scans from nearly the same spot add nothing new. */
const DISC_GRID_METERS = 4;
const VISUAL_DISC_GRID_METERS = 8;
/** Visual margin: the submarine this close to a chamber's volume is "in" it. */
const VISUAL_NODE_MARGIN_METERS = 1.5;
/** Fraction of a passage's length shown from a discovered end whose other end is still unknown (a hint that the passage continues). */
export const PASSAGE_STUB_FRACTION = 0.35;

export type Disc = { x: number; y: number; z: number; r: number };
export type Point3 = { x: number; y: number; z: number };

export type RevealGraph = {
  nodes: Map<string, TileJsonNode>;
  connections: AggregatedConnection[];
  /** Centre line of each connection (parallel to `connections`); empty when an endpoint node is unknown. */
  lines: Vec3T[][];
};

export const buildRevealGraph = (nodeEntries: ReadonlyArray<{ node: TileJsonNode; tileId: string }>, connections: AggregatedConnection[]): RevealGraph => {
  const nodes = new Map<string, TileJsonNode>();
  for (const e of nodeEntries) nodes.set(`${e.tileId}:${e.node.id}`, e.node);
  const lines = connections.map((conn) => {
    const from = nodes.get(conn.fromKey);
    const to = conn.deadEnd ? null : nodes.get(conn.toKey) ?? null;
    if (!from || (!to && !conn.deadEnd)) return [];
    return connectionPolyline(conn, from, to);
  });
  return { nodes, connections, lines };
};

// ---- reveal state encoding --------------------------------------------------------------------------------------------------

const snap = (v: number, grid: number): number => Math.round(v / grid) * grid;

export const encodeDisc = (d: Disc, grid = DISC_GRID_METERS): string => `${DISC_PREFIX}${snap(d.x, grid)}|${snap(d.y, grid)}|${snap(d.z, grid)}|${Math.round(d.r)}`;

export const decodeDisc = (entry: string): Disc | null => {
  if (!entry.startsWith(DISC_PREFIX)) return null;
  const [x, y, z, r] = entry.slice(1).split('|').map(Number);
  return [x, y, z, r].every(Number.isFinite) ? { x, y, z, r } : null;
};

export const parseDiscs = (revealed: ReadonlySet<string>): Disc[] => {
  const out: Disc[] = [];
  for (const entry of revealed) {
    const d = decodeDisc(entry);
    if (d) out.push(d);
  }
  return out;
};

export type Reveal = { nodes: string[]; discs: Disc[] };

/** Adds a reveal to the state; true when anything new was added. */
export const applyReveal = (revealed: Set<string>, reveal: Reveal, discGrid = DISC_GRID_METERS): boolean => {
  let changed = false;
  for (const key of reveal.nodes) {
    if (!revealed.has(key)) { revealed.add(key); changed = true; }
  }
  for (const d of reveal.discs) {
    const entry = encodeDisc(d, discGrid);
    if (!revealed.has(entry)) { revealed.add(entry); changed = true; }
  }
  return changed;
};

// ---- the rules --------------------------------------------------------------------------------------------------------------

export type ScanArgs = {
  graph: RevealGraph;
  scanType: ScanType;
  origin: Point3;
  /** Ping range in metres (= map units). */
  range: number;
  /** Ray hits of a terrain ping (same space as `origin`). */
  hits?: ReadonlyArray<Point3>;
};

const withinAnyPassage = (graph: RevealGraph, p: Point3, margin: number): boolean => {
  for (let i = 0; i < graph.connections.length; i += 1) {
    const line = graph.lines[i];
    if (line.length < 2) continue;
    if (polylineClosest(line, p.x, p.y, p.z).dist <= graph.connections[i].width / 2 + margin) return true;
  }
  return false;
};

export const computeScanReveal = ({ graph, scanType, origin, range, hits }: ScanArgs): Reveal => {
  const reveal: Reveal = { nodes: [], discs: [] };
  if (scanType === 'resource') return reveal;

  if (scanType === 'subsurface') {
    for (const [key, node] of graph.nodes) {
      if (nodeVolumeDistance(node, origin.x, origin.y, origin.z) <= range) reveal.nodes.push(key);
    }
    for (let i = 0; i < graph.connections.length; i += 1) {
      if (graph.lines[i].length >= 2 && polylineSphereSpans(graph.lines[i], origin.x, origin.y, origin.z, range).length > 0) {
        reveal.discs.push({ x: origin.x, y: origin.y, z: origin.z, r: range });
        break;
      }
    }
    return reveal;
  }

  // terrain
  if (hits && hits.length > 0) {
    for (const hit of hits) {
      for (const [key, node] of graph.nodes) {
        if (nodeVolumeDistance(node, hit.x, hit.y, hit.z) <= HIT_NODE_MARGIN_METERS && !reveal.nodes.includes(key)) reveal.nodes.push(key);
      }
      for (let i = 0; i < graph.connections.length; i += 1) {
        const line = graph.lines[i];
        if (line.length < 2) continue;
        const half = graph.connections[i].width / 2;
        if (polylineClosest(line, hit.x, hit.y, hit.z).dist <= half + HIT_PASSAGE_MARGIN_METERS) {
          reveal.discs.push({ x: hit.x, y: hit.y, z: hit.z, r: half + HIT_DISC_PADDING_METERS });
        }
      }
    }
    return reveal;
  }
  return floodReveal(graph, origin, range);
};

/**
 * Fallback terrain rule when there are no ray hits: the open-water network reachable from the submarine along connected passages
 * within `range` metres of travel (and straight-line range), i.e. what a pulse travelling down the tunnels would meet.
 */
const floodReveal = (graph: RevealGraph, origin: Point3, range: number): Reveal => {
  const reveal: Reveal = { nodes: [], discs: [] };
  const dist = new Map<string, number>();
  const queue: Array<[string, number]> = [];
  const seed = (key: string, d: number): void => {
    if (d > range || (dist.get(key) ?? Infinity) <= d) return;
    dist.set(key, d);
    queue.push([key, d]);
  };
  for (const [key, node] of graph.nodes) if (nodeVolumeDistance(node, origin.x, origin.y, origin.z) <= 1) seed(key, 0);
  // Inside a passage: reach both ends along it.
  for (let i = 0; i < graph.connections.length; i += 1) {
    const line = graph.lines[i], conn = graph.connections[i];
    if (line.length < 2) continue;
    const near = polylineClosest(line, origin.x, origin.y, origin.z);
    if (near.dist > conn.width / 2 + 2) continue;
    const total = polylineLength(line);
    pushFlooded(reveal, line, Math.max(0, near.s - range), Math.min(total, near.s + range), conn.width);
    seed(conn.fromKey, near.s);
    if (!conn.deadEnd) seed(conn.toKey, total - near.s);
  }
  while (queue.length > 0) {
    queue.sort((a, b) => a[1] - b[1]);
    const [key, d] = queue.shift()!;
    const node = graph.nodes.get(key);
    if (!node || nodeVolumeDistance(node, origin.x, origin.y, origin.z) > range) continue;
    if (!reveal.nodes.includes(key)) reveal.nodes.push(key);
    for (let i = 0; i < graph.connections.length; i += 1) {
      const conn = graph.connections[i], line = graph.lines[i];
      if (line.length < 2) continue;
      const fromHere = conn.fromKey === key, toHere = !conn.deadEnd && conn.toKey === key;
      if (!fromHere && !toHere) continue;
      const total = polylineLength(line);
      const budget = range - d;
      if (fromHere) pushFlooded(reveal, line, 0, Math.min(total, budget), conn.width);
      else pushFlooded(reveal, line, Math.max(0, total - budget), total, conn.width);
      if (total <= budget) seed(fromHere ? conn.toKey : conn.fromKey, d + total);
    }
  }
  return reveal;
};

/** Discs along the arc-length interval [s0, s1] of a polyline (every ~6 m), each wide enough to cover the passage. */
const pushFlooded = (reveal: Reveal, line: Vec3T[], s0: number, s1: number, width: number): void => {
  if (s1 <= s0) return;
  const step = 6;
  let walked = 0;
  for (let i = 1; i < line.length; i += 1) {
    const a = line[i - 1], b = line[i];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
    for (let s = Math.max(s0, walked); s <= Math.min(s1, walked + len) + 1e-6; s += step) {
      const t = len > 1e-9 ? (s - walked) / len : 0;
      reveal.discs.push({ x: a[0] + (b[0] - a[0]) * t, y: a[1] + (b[1] - a[1]) * t, z: a[2] + (b[2] - a[2]) * t, r: width / 2 + step });
    }
    walked += len;
  }
};

/** Reveal from the submarine's own presence (call every snapshot; cheap when nothing is near). */
export const computeVisualReveal = (graph: RevealGraph, sub: Point3): Reveal => {
  const reveal: Reveal = { nodes: [], discs: [] };
  let inside = false;
  for (const [key, node] of graph.nodes) {
    if (nodeVolumeDistance(node, sub.x, sub.y, sub.z) <= VISUAL_NODE_MARGIN_METERS) {
      reveal.nodes.push(key);
      inside = true;
    }
  }
  if (inside || withinAnyPassage(graph, sub, 2)) reveal.discs.push({ x: sub.x, y: sub.y, z: sub.z, r: VISUAL_REACH_METERS });
  return reveal;
};

export { VISUAL_DISC_GRID_METERS };

// ---- what to draw -----------------------------------------------------------------------------------------------------------

export type PassageDrawPlan = {
  /** Pieces drawn solid: the whole line when both ends are known, otherwise the swept stretches. */
  solid: Vec3T[][];
  /** The faded hint leaving a discovered end whose far end is unknown (empty when solid already covers it). */
  stub: Vec3T[];
  /** The line to stub runs along was reversed (the stub starts at the polyline's end). */
  stubFromEnd: boolean;
  /** Both ends discovered: the whole passage is drawn. */
  complete: boolean;
};

const stubSpan = (total: number, fromStart: boolean): [number, number] =>
  fromStart ? [0, total * PASSAGE_STUB_FRACTION] : [total * (1 - PASSAGE_STUB_FRACTION), total];


/**
 * Decide what part of one connection to draw given the reveal state.
 * `discs` is parseDiscs(revealed); `fromKnown` / `toKnown` say whether the end nodes are discovered (dead ends have no far node).
 */
export const planPassageDraw = (line: Vec3T[], discs: ReadonlyArray<Disc>, fromKnown: boolean, toKnown: boolean, deadEnd: boolean): PassageDrawPlan => {
  const none: PassageDrawPlan = { solid: [], stub: [], stubFromEnd: false, complete: false };
  if (line.length < 2) return none;
  if (fromKnown && toKnown && !deadEnd) return { solid: [line], stub: [], stubFromEnd: false, complete: true };
  const spans: Array<[number, number]> = [];
  for (const d of discs) for (const s of polylineSphereSpans(line, d.x, d.y, d.z, d.r)) spans.push(s);
  const merged = mergeSpans(spans);
  const solid = clipPolylineToSpans(line, merged);
  let stub: Vec3T[] = [];
  let stubFromEnd = false;
  if (fromKnown || (toKnown && !deadEnd)) {
    const total = polylineLength(line);
    const [s0, s1] = stubSpan(total, fromKnown);
    const covered = merged.some(([a, b]) => a <= s0 + 0.5 && b >= s1 - 0.5);
    if (!covered) {
      stub = clipPolylineToSpans(line, [[s0, s1]])[0] ?? [];
      stubFromEnd = !fromKnown;
    }
  }
  return { solid, stub, stubFromEnd, complete: false };
};
